import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../../lib/api";

// Tipos espelham apps/api/src/carmillion_api/routers/kanban.py (Quadro v2).
export type Pipe = "projetos" | "tasks";
export const PIPE_LABEL: Record<Pipe, string> = { projetos: "Projetos", tasks: "Tasks" };

export interface Ref { id: string; name: string }

export interface KCard {
  id: string;
  pipe: Pipe;
  stage: string;
  title: string;
  tipo: string | null;
  tipo_label: string | null;
  subject: Ref | null;
  agent: Ref | null;
  owner: Ref | null;
  participants: Ref[];
  due_date: string | null;
  due_time: string | null;
  overdue: boolean;
  source: "manual" | "api";
  source_system: string | null;
  created_by: Ref | null;
  created_at: string;
  stage_changed_at: string;
}

export interface KEvent {
  id: string;
  at: string;
  by: string;
  kind: "created" | "stage" | "field" | "participant_add" | "participant_remove" | "attachment_add" | "attachment_remove";
  data: Record<string, any>;
}

export interface KAttachment {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  uploaded_by: Ref | null;
  created_at: string;
}

export interface KDetail extends KCard {
  description: string;
  attachments: KAttachment[];
  events: KEvent[];
  time_by_stage: { stage: string; seconds: number }[];
  age_seconds: number;
  can_edit: boolean;
  can_delete: boolean;
  can_pick_owner: boolean;
}

export interface KSubject { id: string; tipo: string; tipo_label: string; name: string; active: boolean }

export interface KOptions {
  pipes: Record<Pipe, string[]>;
  tipos: { id: string; label: string }[];
  subjects: KSubject[];
  agents: Ref[];
  me: Ref;
  can_pick_owner: boolean;
  can_delete: boolean;
  can_manage_subjects: boolean;
}

export function useKanbanOptions() {
  return useQuery({ queryKey: ["kanban", "options"], queryFn: () => api.get<KOptions>("/kanban/options") });
}

export function kanbanAttachmentUrl(cardId: string, attId: string) {
  return `/api/kanban/${cardId}/attachments/${attId}`;
}

// Mesmo esquema do Tarefas: body raw, nome no header (fora do `api`, que força JSON e 15s).
export async function uploadKanbanAttachment(cardId: string, file: File) {
  const resp = await fetch(`/api/kanban/${cardId}/attachments`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type, "x-filename": encodeURIComponent(file.name) },
    body: file,
  });
  if (!resp.ok) {
    let body: unknown = null;
    try { body = await resp.json(); } catch {}
    throw new ApiError(resp.status, body);
  }
}

export function fmtDuration(sec: number) {
  if (sec < 60) return "< 1 min";
  const d = Math.floor(sec / 86_400), h = Math.floor((sec % 86_400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
}

export function eventText(e: KEvent): string {
  const d = e.data;
  switch (e.kind) {
    case "created": return `Criado em “${d.stage}” · dono ${d.owner}${d.participants?.length ? ` · participantes ${d.participants.join(", ")}` : ""}`;
    case "stage": return `Movido de “${d.from}” para “${d.to}”`;
    case "field":
      if (d.field === "description") return "Descrição alterada";
      return `${d.label}: ${d.from ?? "—"} → ${d.to ?? "—"}`;
    case "participant_add": return `Participante adicionado: ${d.name}`;
    case "participant_remove": return `Participante removido: ${d.name}`;
    case "attachment_add": return `Anexo adicionado: ${d.filename}`;
    case "attachment_remove": return `Anexo removido: ${d.filename}`;
  }
}
